import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Button, Table } from 'react-daisyui';
import { QBOCategory, QBODetailedTransaction } from '@shared/types/qbo_data';
import { getPageRecords } from '@/utils';
import { DashboardCard } from '@/components/DashboardCard';
import { OSPagination } from '@/components/Pagination/Pagination';
import { SortArrow } from '@/components/SortArrow/SortArrow';
import { useTableSort } from '@/components/Table/useTableSort';
import { formatCategoryLabel } from './categoryLabel';
import {
  CategoryPicker,
  ClipText,
  ClipValue,
  formatDate,
  getAmount,
  MemoActionSelect,
  usdFormatter,
} from './transactionsComponents';
import './TableTransactionsCompact.css';

function PushToQBOButton({
  canPush,
  onPush,
  testid,
}: {
  canPush: boolean;
  onPush: () => void;
  testid?: string;
}) {
  return (
    <Button
      size='sm'
      data-testid={testid}
      className={`btn-accent ${canPush ? '' : 'btn-disabled'}`}
      onClick={onPush}
    >
      Push to QBO
    </Button>
  );
}

function CompactRow({
  transaction,
  reviewed,
  onMemoChange,
  onCategoryChange,
  selected,
  eligible,
  onToggleSelect,
  onPushToQBO,
}: {
  transaction: QBODetailedTransaction;
  reviewed: QBODetailedTransaction;
  onMemoChange: (keepOriginal: boolean) => void;
  onCategoryChange: (category: QBOCategory | undefined) => void;
  selected: boolean;
  eligible: boolean;
  onToggleSelect: (id: number, checked: boolean) => void;
  onPushToQBO: (ids: number[]) => void;
}) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const amount = getAmount(transaction);

  const hasClientMemo = !!reviewed.modified_memo;
  const keepOriginal = reviewed.keep_original_memo ?? false;

  // Can only push once there's a reviewed category (that's what goes to QBO).
  const canPush = eligible;

  const onPush = () => {
    if (!canPush) return;
    onPushToQBO([transaction.id as number]);
  };

  return (
    <>
      <tr
        className='cursor-pointer'
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        data-testid={`cmp-tr-${transaction.id}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <td className='w-[38px] text-center'>
          <input
            type='checkbox'
            className='checkbox checkbox-sm align-middle'
            checked={selected}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              onToggleSelect(transaction.id as number, e.target.checked)
            }
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            data-testid={`cmp-select-${transaction.id}`}
          />
        </td>
        <td className='w-[34px] text-center'>
          <FontAwesomeIcon
            icon={faChevronRight}
            className={`transition-transform ${
              isOpen ? 'rotate-90 text-secondary' : 'text-base-content/50'
            }`}
          />
        </td>
        <td>
          <div className='flex items-center gap-2'>
            {transaction.tries ? (
              <span
                className='tooltip tooltip-top cmp-tip text-error'
                data-tip='There was an error updating this transaction in QBO.'
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                data-testid={`cmp-error-${transaction.id}`}
              >
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </span>
            ) : null}
            <ClipText value={transaction.payee} width={150} />
          </div>
        </td>
        <td>
          <ClipText value={transaction.memo} width={240} />
        </td>
        <td
          className={`tabular-nums font-semibold whitespace-nowrap text-right ${
            amount >= 0 ? 'text-green-500' : 'text-red-500'
          }`}
        >
          {usdFormatter.format(amount)}
        </td>
        <td className='whitespace-nowrap'>{transaction.bank || '—'}</td>
        <td className='whitespace-nowrap'>{formatDate(transaction.date)}</td>
      </tr>
      {isOpen ? (
        <tr>
          <td colSpan={7} className='cmp-detail-cell'>
            <div className='grid gap-3.5 rounded-xl border border-base-content/20 bg-base-content/5 p-3.5'>
              <div className='grid grid-cols-1 gap-3.5 sm:grid-cols-2'>
                <div className='grid min-w-0 gap-2 rounded-lg border border-base-content/10 p-3'>
                  <div className='cmp-title'>Original (QBO)</div>
                  <div className='cmp-kv'>
                    <span className='cmp-label'>Memo</span>
                    <ClipValue value={transaction.memo} />
                  </div>
                  <div className='cmp-kv'>
                    <span className='cmp-label'>Category</span>
                    <ClipValue
                      value={formatCategoryLabel(transaction.qbo_category)}
                    />
                  </div>
                </div>
                <div className='grid min-w-0 gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3'>
                  <div className='cmp-title cmp-title--client'>
                    Client suggested
                  </div>
                  <div className='cmp-kv'>
                    <span className='cmp-label'>Memo</span>
                    <ClipValue value={transaction.modified_memo} />
                  </div>
                  <div className='cmp-kv'>
                    <span className='cmp-label'>Category</span>
                    <ClipValue
                      value={formatCategoryLabel(
                        transaction.modified_qbo_category
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className='grid gap-2.5 rounded-lg border border-secondary/40 bg-secondary/5 p-3'>
                <div className='cmp-title cmp-title--reviewer'>Your review</div>
                <div className='cmp-controls'>
                  <div className='grid gap-1'>
                    <span className='cmp-label'>Approve memo</span>
                    <MemoActionSelect
                      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                      id={`cmp-memo-select-${transaction.id}`}
                      className='w-full'
                      keepOriginal={keepOriginal}
                      disabled={!hasClientMemo}
                      onChange={onMemoChange}
                    />
                  </div>
                  <div className='cmp-category grid gap-1'>
                    <span className='cmp-label'>Set category</span>
                    <CategoryPicker
                      transaction={transaction}
                      value={
                        reviewed.reviewed_qbo_categories_id
                          ? formatCategoryLabel(reviewed.reviewed_qbo_category)
                          : undefined
                      }
                      onSelected={onCategoryChange}
                    />
                  </div>
                  <PushToQBOButton
                    canPush={canPush}
                    onPush={onPush}
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    testid={`cmp-push-to-qbo-${transaction.id}`}
                  />
                </div>
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function TableTransactionsForControllers({
  transactions,
  selectedIds = new Set<number>(),
  onToggleSelect = () => {},
  onSelectMany = () => {},
  onSaveReview = () => {},
  onPushToQBO = () => {},
}: {
  transactions: QBODetailedTransaction[];
  selectedIds?: Set<number>;
  onToggleSelect?: (id: number, checked: boolean) => void;
  onSelectMany?: (ids: number[], checked: boolean) => void;
  onSaveReview?: (reviewed: QBODetailedTransaction) => void;
  onPushToQBO?: (ids: number[]) => void;
}) {
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
  const { sort, sortBy, sortData } = useTableSort<QBODetailedTransaction>({
    field: 'payee',
    asc: true,
  });

  useEffect(() => {
    setSelectedPage(1);
  }, [recordsPerPage, transactions, sort]);

  const pageRecords = getPageRecords(
    sortData(transactions),
    selectedPage,
    recordsPerPage
  ) as QBODetailedTransaction[];

  // Effective review for a row (pure): the transaction seeded with the memo
  // default. The persisted values live in qboData, updated by onSaveReview.
  const getReviewed = (t: QBODetailedTransaction): QBODetailedTransaction => ({
    ...t,
    // With a client memo, default to "push client memo" (false); without one,
    // there's nothing to push so it must keep the original QBO memo (true).
    keep_original_memo: t.modified_memo ? t.keep_original_memo ?? false : true,
  });

  // Any row can be selected (for a bulk edit); a reviewed category is only
  // required to push it to QBO, which the floating bar enforces separately.
  const isEligible = (t: QBODetailedTransaction) =>
    !!t.reviewed_qbo_categories_id;
  const pageIds = pageRecords.map((t) => t.id as number);
  const allSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const handleMemoChange = (t: QBODetailedTransaction, keep: boolean) => {
    const updatedTransaction = { ...getReviewed(t), keep_original_memo: keep };
    onSaveReview(updatedTransaction);
  };

  const handleCategoryChange = (
    t: QBODetailedTransaction,
    category: QBOCategory | undefined
  ) => {
    const updatedTransaction = {
      ...getReviewed(t),
      reviewed_qbo_categories_id: category?.id || undefined,
      reviewed_qbo_category: {
        name: category?.name || '',
        external_id: category?.external_id,
      },
    };
    onSaveReview(updatedTransaction);
    // Clearing the category makes the row ineligible → drop it from selection.
    if (!category?.id) onToggleSelect(t.id as number, false);
  };

  const headerCell = (label: string, field?: keyof QBODetailedTransaction) => (
    <div
      role='button'
      tabIndex={0}
      className={field ? 'cursor-pointer' : 'pointer-events-none'}
      onClick={() => field && sortBy(field)}
    >
      <span className='text-base font-bold capitalize'>
        {label}
        {field ? <SortArrow sort={sort} fieldName={field} /> : null}
      </span>
    </div>
  );

  return (
    <DashboardCard className='mt-0 p-0 min-h-fit' cardBodyclassName='p-0'>
      <Table
        zebra
        id='table-transactions-compact'
        data-testid='transactions-compact'
      >
        <Table.Head>
          <span className='text-center'>
            <input
              type='checkbox'
              className='checkbox checkbox-sm align-middle'
              checked={allSelected}
              disabled={pageIds.length === 0}
              onChange={(e) => onSelectMany(pageIds, e.target.checked)}
              aria-label='select all'
              data-testid='cmp-select-all'
            />
          </span>
          <span aria-label='expand' />
          {headerCell('Payee', 'payee')}
          {headerCell('Memo', 'memo')}
          {headerCell('Amount')}
          {headerCell('Bank', 'bank')}
          {headerCell('Date', 'date')}
        </Table.Head>
        <tbody>
          {pageRecords.map((t) => (
            <CompactRow
              key={`cmp-${t.txn_id}-${t.qbo_company_id as number}`}
              transaction={t}
              reviewed={getReviewed(t)}
              onMemoChange={(keep) => handleMemoChange(t, keep)}
              onCategoryChange={(category) => handleCategoryChange(t, category)}
              selected={selectedIds.has(t.id as number)}
              eligible={isEligible(t)}
              onToggleSelect={onToggleSelect}
              onPushToQBO={onPushToQBO}
            />
          ))}
        </tbody>
      </Table>
      <OSPagination
        totalRecords={transactions.length}
        numPages={Math.ceil(transactions.length / recordsPerPage)}
        selectedPage={selectedPage}
        rowsPerPage={recordsPerPage}
        selectedRowsPerPage={[10, 20, 30, 40, 50]}
        onPageSelect={setSelectedPage}
        onSelectRowsChange={setRecordsPerPage}
      />
    </DashboardCard>
  );
}

export default TableTransactionsForControllers;
export { TableTransactionsForControllers };
